import React, { useState, FunctionComponent, useEffect, useCallback } from 'react';
import { Presentation } from './presentation';
import { EducationalHistories, CareerHistories, CareerEmploymentType } from '../../../types/personShow';
import { PersonShowModalHistoriesState } from './types';
import { fetchOccupations } from '../../../services/personProfiles';
import { getSalaryRangeText } from '../../../libs/salaryUtils';
import { scrollToElementById } from '../../../libs/scrollUtils';
import { EMPLOYMENT_TYPE_MAP } from './constants';
import {
  requiresSalesDetailWithManager,
} from '../../../libs/occupationValidation';

type Props = {
  onChangedBody: (name: string, value: any) => void;
  histories: EducationalHistories[] | CareerHistories[];
  clickTarget: number;
  startYearList: number[];
  birthdayList: number[];
  endYearList: number[];
  careerEmploymentTypes?: CareerEmploymentType[];
  target: string;
  handleDeleteHistories: () => void;
  fromEntry?: boolean;
  onRegisterValidationHandler?: (handler: () => boolean) => void;
};

export const Component: FunctionComponent<Props> = ({
  onChangedBody,
  histories,
  clickTarget,
  startYearList,
  birthdayList,
  endYearList,
  careerEmploymentTypes,
  target,
  handleDeleteHistories,
  fromEntry,
  onRegisterValidationHandler,
}) => {
  const [state, setState] = useState<PersonShowModalHistoriesState>({
    id: 0, // ダミー値
    name: '',
    department: '',
    description: '',
    descriptionRaw: '',
    startDateYear: '',
    startDateMonth: '',
    endDateYear: '',
    endDateMonth: '',
    title: target === 'careerHistories' ? '職歴を入力して保存してください' : '学歴を入力して保存してください',
    semititleFirst: target === 'careerHistories' ? '会社名' : '学校名',
    semititleSecond: target === 'careerHistories' ? '役職' : '学部・学科・専攻',
    semititleThird: target === 'careerHistories' ? '業務内容' : '学んだこと・研究結果など',
    careerEmploymentTypeId: null,
    position: [],
    salaryRange: '',
    currentWorking: false,
    modalSmall: false,
    modalSmallSub: false,
    suggestOccupations: [],
    suggestOccupationDetails: [],
    salaries: [],
    occupationCount: 0,
    positionIndex: 0,
  });

  const selectableEmploymentTypes = (careerEmploymentTypes ?? [])
    .filter((cet) => cet.position !== null);

  // マスタ取得はマウント時のみ。histories の参照変更（入力ごとの親更新）では再取得しない
  useEffect(() => {
    let ignore = false;
    fetchOccupations()
      .then((response) => {
        if (ignore) return;
        setState(prev => ({
          ...prev,
          suggestOccupations: response.suggestOccupations,
          suggestOccupationDetails: response.suggestOccupationDetails,
          salaries: response.salaries
        }));
      })
      .catch((error) => {
        if (!ignore) console.log(error);
      });
    return () => {
      ignore = true;
    };
  }, []);

  const fetchDefaultData = useCallback(() => {
    const item = histories[clickTarget];
    if (!item) return;
    const [startYear, startMonthRaw] = item.startDate?.split('/') || ['', ''];
    const [endYear, endMonthRaw] = item.endDate?.split('/') || ['', ''];
    const startMonth = startMonthRaw === undefined || startMonthRaw === '' ? '' : String(Number(startMonthRaw));
    const endMonth = endMonthRaw === undefined || endMonthRaw === '' ? '' : String(Number(endMonthRaw));
    const newState: Partial<PersonShowModalHistoriesState> = {
      name: item.name || '',
      department: item.department || '',
      description: item.description || '',
      descriptionRaw: item.descriptionRaw || '',
      startDateYear: startYear || '',
      startDateMonth: startMonth || '',
      endDateYear: endYear || '',
      endDateMonth: endMonth || '',
    };
    if (target === 'careerHistories') {
      const careerItem = item as CareerHistories;
      newState.careerEmploymentTypeId = careerItem.careerEmploymentTypeId || null;
      newState.position = careerItem.position || [];
      newState.salaryRange = careerItem.salaryRange || '';
      newState.currentWorking = careerItem.currentWorking || false;
      newState.occupationCount = (careerItem.position?.length || 1) - 1;
    }
    setState((prev) => ({ ...prev, ...newState }));
    if (newState.currentWorking) {
      $('.endDates').addClass('active')
    } else {
      $('.endDates').removeClass('active')
    };
  }, [histories, clickTarget, target]);

  useEffect(() => {
    fetchDefaultData();
  }, [fetchDefaultData]);

  const handleChangeDate = (name: keyof PersonShowModalHistoriesState, event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.currentTarget.value;
    setState(prev => ({ ...prev, [name]: value, }));
    handleFetchHashForUpdate(name, value);
  };

  const handleChangeBody = (
    name: keyof PersonShowModalHistoriesState,
    value: any
  ) => {
    setState(prev => {
      const newState: Partial<PersonShowModalHistoriesState> = { ...prev };
  
      if (name === 'currentWorking') {
        const isCurrentlyWorking = value as boolean;
        newState.currentWorking = isCurrentlyWorking;
        newState.endDateYear = isCurrentlyWorking ? '' : prev.endDateYear;
        newState.endDateMonth = isCurrentlyWorking ? '' : prev.endDateMonth;
        handleFetchHashForUpdate('currentWorking', isCurrentlyWorking);
        if (isCurrentlyWorking) {
          $('.endDates').addClass('active');
        } else {
          $('.endDates').removeClass('active');
        }
      } else if (name === 'salaryRange') {
        const salaryId = Number(value.currentTarget.dataset.id);
        newState.salaryRange = salaryId;
        handleFetchHashForUpdate('salaryRange', salaryId);
      } else if (name === 'description') {
        newState.description = value;
        newState.descriptionRaw = value;
        handleFetchHashForUpdate('description', value);
      } else {
        newState[name] = value;
        handleFetchHashForUpdate(name, value);
      }
      return newState as PersonShowModalHistoriesState;
    });
  };

  const handleChangeEmploymentSpecial = (type: string, checked: boolean) => {
    if (checked) {
      const id = EMPLOYMENT_TYPE_MAP[type];
      handleChangeBody('careerEmploymentTypeId', id);
    } else {
      handleChangeBody('careerEmploymentTypeId', null);
    }
  };

  const handleClickOccupations = (occupationName: string, occupationDetailId: number | string, occupationDetailName: string) => {
    setState(prev => {
      const newState = { ...prev };
      const index = prev.positionIndex;
      const currentPositions = [...prev.position];

      const newPosition = occupationDetailId
        ? { occupationName, occupationDetailId, occupationDetailName }
        : { occupationName: occupationName, occupationDetailId: '', occupationDetailName: '' };

      if (currentPositions[index] === undefined) {
        currentPositions.push(newPosition);
      } else {
        currentPositions.splice(index, 1, newPosition);
      }

      newState.position = currentPositions;
      handleFetchHashForUpdate('position', currentPositions);
      return newState;
    });
  };

  const handleFetchHashForUpdate = (name: string, value: any) => {
    const fetchTarget = () => {
      if (target === 'educationalHistories') {
        return {
          id: state.id,
          name: state.name,
          department: state.department,
          description: state.description,
          descriptionRaw: state.descriptionRaw,
          startDate: `${state.startDateYear}/${state.startDateMonth}`,
          endDate: `${state.endDateYear}/${state.endDateMonth}`
        };
      } else {
        return {
          id: state.id,
          name: state.name,
          department: state.department,
          description: state.description,
          descriptionRaw: state.descriptionRaw,
          startDate: `${state.startDateYear}/${state.startDateMonth}`,
          endDate: `${state.endDateYear}/${state.endDateMonth}`,
          careerEmploymentTypeId: state.careerEmploymentTypeId,
          position: state.position,
          salaryRange: state.salaryRange,
          currentWorking: state.currentWorking
        };
      }
    };

    const insertHistory = fetchTarget();

    if (name.includes('startDateYear')) {
      insertHistory.startDate = `${value}/${state.startDateMonth}`;
    } else if (name.includes('startDateMonth')) {
      insertHistory.startDate = `${state.startDateYear}/${value}`;
    } else if (name.includes('endDateYear')) {
      insertHistory.endDate = `${value}/${state.endDateMonth}`;
    } else if (name.includes('endDateMonth')) {
      insertHistory.endDate = `${state.endDateYear}/${value}`;
    } else {
      (insertHistory as any)[name] = value;
      if (name === 'description') {
        (insertHistory as any)['descriptionRaw'] = value;
      }
    }

    const updatedHistories = [...histories];
    if (clickTarget !== -1 && updatedHistories.length !== 0) {
      updatedHistories.splice(clickTarget, 1, insertHistory);
    } else {
      updatedHistories.push(insertHistory);
    }
  
    onChangedBody(target, updatedHistories);
  };

  const handleOpenSmallModal = (index: number) => {
    setState(prev => ({ ...prev, modalSmall: !state.modalSmall, positionIndex: index ?? 0 }));
  };

  const handleOpenSmallSubModal = () => {
    setState(prev => ({ ...prev, modalSmallSub: !state.modalSmallSub }));
  };

  const handleCloseSmallModal = () => {
    setState(prev => ({ ...prev, modalSmall: !state.modalSmall }));
  };

  const handleCloseSmallSubModal = () => {
    setState(prev => ({ ...prev, modalSmallSub: !state.modalSmallSub }));
  };

  const onDeleteHistories = () => {
    if (!confirm('内容をすべて削除します。元に戻せませんが本当に削除してもよろしいですか？')) {
      return false
    };
    setState(prev => ({
      ...prev,
      name: '',
      department: '',
      description: '',
      descriptionRaw: '',
      startDateYear: 2024,
      startDateMonth: 1,
      endDateYear: 2024,
      endDateMonth: 1
    }));
    if (target === 'careerHistories') {
      setState(prev => ({
        ...prev,
        careerEmploymentTypeId: null,
        position: [],
        salaryRange: '',
        currentWorking: false,
      }));
    }
    handleDeleteHistories();
  };

  const handleAddOccuption = () => {
    setState(prev => ({ ...prev, occupationCount: state.occupationCount + 1 }));
  }

  const handleDeleteOccuption = (index: number) => {
    setState(prev => ({ ...prev, occupationCount: state.occupationCount - 1 }));
    const currentPositions = state.position;
    currentPositions.splice(index, 1)
    setState(prev => ({ ...prev, position: currentPositions }));
  };

  const handleScrollTo = (name: string) => {
    scrollToElementById(`flexbox_${name}`);
  };

  const handleClearSalary = () => {
    setState(prev => ({ ...prev, salaryRange: '' }));
  };

  const isCheckedPosition = (parent: boolean, value: string | number): boolean => {
    const position = state.position[state.positionIndex];
    return parent
      ? position?.occupationName === value
      : position?.occupationDetailId === value;
  };

  const validateOccupations = useCallback(() => {
    const selectedDetailIds = state.position.map(({ occupationDetailId }) => occupationDetailId);
    if (!requiresSalesDetailWithManager(selectedDetailIds, state.suggestOccupationDetails)) {
      return true;
    }

    return false;
  }, [state.position, state.suggestOccupationDetails]);

  useEffect(() => {
    onRegisterValidationHandler?.(validateOccupations);
  }, [onRegisterValidationHandler, validateOccupations]);

  return (
    <Presentation
      state={state}
      startYearList={startYearList}
      birthdayList={birthdayList}
      careerEmploymentTypes={selectableEmploymentTypes}
      target={target}
      fromEntry={fromEntry}
      onChangeBody={handleChangeBody}
      onChangeEmploymentSpecial={handleChangeEmploymentSpecial}
      handleOpenSmallModal={handleOpenSmallModal}
      handleDeleteOccuption={handleDeleteOccuption}
      handleAddOccuption={handleAddOccuption}
      handleScrollTo={handleScrollTo}
      isCheckedPosition={isCheckedPosition}
      salaryWord={() =>
        typeof state.salaryRange === 'number'
          ? getSalaryRangeText(state.salaryRange, 'タップして選択してください')
          : 'タップして選択してください'
      }
      handleClickOccupations={handleClickOccupations}
      handleCloseSmallModal={handleCloseSmallModal}
      handleOpenSmallSubModal={handleOpenSmallSubModal}
      handleClearSalary={handleClearSalary}
      handleCloseSmallSubModal={handleCloseSmallSubModal}
      onChangeDate={handleChangeDate}
      handleDeleteHistories={onDeleteHistories}
    />
  );
};
